import type { ReactNode } from 'react';

import { DimeCardShell } from './DimeCardShell.js';
import { DimeSkeleton } from './DimeSkeleton.js';

type DimeCardShellSkeletonProps = Readonly<{
  factCount?: number;
  actionCount?: number;
  tabCount?: number;
  rowCount?: number;
  content?: ReactNode;
}>;

/** Meta items are keyed by label, so each placeholder needs a distinct, non-rendering one. */
const placeholderLabel = (index: number) => '​'.repeat(index + 1);

function metaPlaceholder(valueWidth: string, index: number) {
  return {
    icon: <DimeSkeleton className="rounded-sm" />,
    label: placeholderLabel(index),
    value: <DimeSkeleton className={`h-3.5 ${valueWidth}`} />,
  };
}

export function DimeCardShellSkeleton({
  factCount = 3,
  actionCount = 2,
  tabCount = 0,
  rowCount = 0,
  content,
}: DimeCardShellSkeletonProps) {
  return (
    <div role="status" aria-busy="true" aria-label="Loading">
      <DimeCardShell
        title={<DimeSkeleton className="h-5 w-48" />}
        status={<DimeSkeleton className="h-5 w-16 rounded-full" />}
        description={<DimeSkeleton className="h-4 w-72" />}
        meta={[
          ...Array.from({ length: factCount }, (_, fact) => metaPlaceholder('w-20', fact)),
          metaPlaceholder('w-40', factCount),
        ]}
        actions={Array.from({ length: actionCount }, (_, action) => (
          <DimeSkeleton key={action} className="h-9 w-24" />
        ))}
        content={
          <>
            {tabCount > 0 ? (
              <div className="-mx-4 flex gap-6 border-y px-4 py-3">
                {Array.from({ length: tabCount }, (_, tab) => (
                  <DimeSkeleton key={tab} className="h-4 w-20" />
                ))}
              </div>
            ) : null}
            {rowCount > 0 ? (
              <div className="space-y-2 py-4">
                {Array.from({ length: rowCount }, (_, row) => (
                  <DimeSkeleton key={row} className="h-9 w-full" />
                ))}
              </div>
            ) : null}
            {content}
          </>
        }
      />
    </div>
  );
}
